<template>
  <div class="container">
    <h1>Dokumente</h1>
    <template v-if="uid">
      <b-pagination
          v-show="documents.length > 0"
          class="float-right"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          aria-controls="documents-table"
      ></b-pagination>
      <b-card class="w-100" style="max-width: 100%; overflow-x: auto;" v-show="documents.length > 0">
        <b-table :items="items" :fields="fields" id="documents-table" :per-page="perPage" :current-page="currentPage">
          <template #cell(pdf)="data">
            <a href="#" @click="download(data.value)">Download</a>
          </template>
        </b-table>
      </b-card>
      <b-pagination
          v-show="documents.length > 0"
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          aria-controls="documents-table"
      ></b-pagination>
      <b-alert v-show="documents.length === 0 && isFetched" show>Bisher sind keine Dokumente vorhanden.</b-alert>
    </template>
    <template v-else>
      <div class="page-loader-wrapper"
           style="position: absolute; transform: translate(-50%, -50%); left: 50%; top: 50%">
        <b-spinner></b-spinner>
      </div>
    </template>
    <b-row>
      <b-col sm="6" md="4">
        <b-select :options="docTypes" v-model="docTypeSelection">
          <option :value="null" disabled>-- Dokumenttyp auswählen --</option>
        </b-select>
      </b-col>
      <b-col sm="6" md="8" class="pt-2 pt-sm-0">
        <b-button :disabled="inProgress || docTypeSelection === null" @click="downloadAllDocs(docTypeSelection, dateFrom, dateTo)">
          Alle <b>{{ docTypeSelection }}</b> herunterladen
          {{ (dateFrom ? ' ab ' + new Date(dateFrom).toLocaleDateString() : ' ') + (dateTo ? ' bis ' + new Date(dateTo).toLocaleDateString() : '') }}
          <b-spinner v-if="inProgress" small></b-spinner>
        </b-button>
      </b-col>
      <b-col md="4" class="pt-2">
        <label>
          Datum von
        </label>
        <b-datepicker label-no-date-selected="Datum auswählen" label-help="" v-model="dateFrom"></b-datepicker>
      </b-col>
      <b-col md="4" class="pt-2">
        <label>
          Datum bis
        </label>
        <b-datepicker label-no-date-selected="Datum auswählen" label-help="" v-model="dateTo"></b-datepicker>
      </b-col>
    </b-row>
  </div>
</template>

<script>

import firestore from "@/api/firestore";
import firebase from "firebase";
import axios from "axios";
import {downloadZip} from "client-zip"
import platformSettings from "~config/settings"

export default {
  data() {
    return {
      documents: [],
      uid: null,
      currentPage: 1,
      lastVisible: {1: null},
      isFetched: false,
      perPage: 20,
      docTypeSelection: null,
      dateTo: null,
      dateFrom: null,
      inProgress: false,
      // docTypes are reflecting the fallback values, those values are overwritten from config/<region>/settings.js
      docTypes: [
        "rechnung_produzent",
        "rechnung_markplatzgebuehr_verkaeufer",
        "order_confirmation_customer",
        "Bestellbestätigung_Verkäufer_geliefert",
        "order_confirmation_vendor",
        "delivery_note_vendor",
        "lieferschein_produzent",
        "monatssammelrechnung",
        "monthly_invoice",
        "order_cancelled",
      ],
      fields: [
        {key: 'order', label: 'Bestellnummer', sortable: true},
        {key: 'type', label: 'Typ', sortable: true},
        {key: 'createdAt', label: 'Erstellt am', sortable: true},
        {key: 'pdf', label: "Download"}
      ]
    }
  },
  computed: {
    totalRows() {
      return this.documents.length === this.perPage ? this.currentPage * this.perPage + 1 : this.currentPage * this.perPage;
    }
  },
  methods: {
    getFileName(pathParts) {
      let fileName = pathParts[pathParts.length - 1]
      if (platformSettings.docNames) {
        const docType = fileName.split('-')[1].replaceAll(' ', '')
        if (platformSettings.docNames[docType]) {
          fileName = fileName.replace(docType, platformSettings.docNames[docType])
        }
      }
      return fileName
    },
    items(context, callback) {
      this.currentPage = context.currentPage
      if (this.uid !== null) {
        let lastVisible = this.lastVisible[context.currentPage] === undefined ? this.documents[this.documents.length - 1] : this.lastVisible[this.currentPage];
        this.lastVisible[context.currentPage] = lastVisible

        this.documents = []
        firestore.getDocuments(this.uid, context.perPage, lastVisible).then(snapshot => {
          snapshot.forEach(docRef => {
            this.documents.push(docRef.data())
          })
          callback(
              this.documents.map(document => ({
                order: document.orderName,
                type: platformSettings.docNames ? platformSettings.docNames[document.type] || document.type : document.type,
                createdAt: this.timestampToString(document.createdAt.seconds),
                pdf: document.pdf
              }))
          )
        }).finally(() => {
          this.isFetched = true
        })
      }
      return null
    },
    async downloadToZip(files, name) {
      const f = []
      for (let i = 0; i < files.length; i++) {
        const pathParts = files[i].split('/')
        let fileName = this.getFileName(pathParts)
        try {
          await firebase.storage().ref(files[i]).getDownloadURL().then(async url => {
            return await axios({method: 'get', url, responseType: 'arraybuffer'}).then(async response => {
              const blob = new Blob([response.data], {type: 'application/pdf'})
              f.push({name: fileName, input: blob, lastModified: new Date()})
            }).catch(error => {
              console.log(error)
              return null
            })
          })
        } catch (e) {
          console.log(e)
        }
      }
      const blob = await downloadZip(f).blob()
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = name + '.zip'
      link.click()
      link.remove()
      this.inProgress = false

      // in real life, don't forget to revoke your Blob URLs if you use them
    },
    downloadAllDocs(type, dateFrom, dateTo) {
      this.inProgress = true
      let df = null
      let dt = null
      let docType = type
      if (dateFrom !== null) {
        const dateInputFrom = new Date(dateFrom).toUTCString()
        df = Math.floor(new Date(dateInputFrom) / 1000);
      }
      if (dateTo !== null) {
        const dateInputTo = new Date(dateTo + ' 23:59:59').toUTCString()
        dt = Math.floor(new Date(dateInputTo) / 1000);
      }
      if (platformSettings.docNames) {
      Object.entries(platformSettings.docNames).forEach((r) => {
          if (r.length > 1 && r[1] === type) {
            docType =  r[0]
          }
        })
      }
      return firestore.getDocumentsFiltered(this.uid, docType, df, dt).then(snapshot => {
        if (snapshot.docs.length === 0) {
          this.inProgress = false
          return
        }
        const files = []
        snapshot.forEach(docRef => {
          const data = docRef.data()
          // @todo replace with firestore query (see api/firestore.js)
          if ((df !== null || dt !== null)) {
            if (df && data.createdAt.seconds >= df && dt === null) {
              files.push(data.pdf)
            } else if (dt && (data.createdAt.seconds <= dt) && df === null) {
              files.push(data.pdf)
            } else if (data.createdAt.seconds <= dt && data.createdAt.seconds >= df) {
              files.push(data.pdf)
            }
          } else if (data.pdf) {
            files.push(data.pdf)
          }
        })
        if (files.length > 0) {
          this.downloadToZip(files, this.docTypeSelection + (this.dateFrom ? '_' + new Date(this.dateFrom).toLocaleDateString() : '') + (this.dateTo ? '_' + new Date(this.dateTo).toLocaleDateString() : ''))
        } else {
          this.inProgress = false
        }
      })
    },
    timestampToString(seconds) {
      let date = new Date(0);
      date.setUTCSeconds(seconds);
      return date.toLocaleDateString()
    },
    download(filePath) {
      const pathParts = filePath.split('/')
      const fileName = this.getFileName(pathParts)
      firebase.storage().ref(filePath).getDownloadURL().then(url => {
        axios({method: 'get', url, responseType: 'arraybuffer'}).then(response => {
          console.log("Downloaded blob")
          const blob = new Blob([response.data], {type: 'application/pdf'})
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(link.href)
        }).catch(error => {
          console.log(error)
        })
      })
    }
  },
  created() {
    if (platformSettings.docTypes) {
      this.docTypes = platformSettings.docTypes
    }
    if (platformSettings.docNames) {
      this.docTypes = this.docTypes.map(e => {
        return  platformSettings.docNames ? platformSettings.docNames[e] || e : e
      })
    }
    firebase.auth().onAuthStateChanged(user => {
      this.uid = user.uid;
    });
  }
}
</script>
